import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, LogOut, School as SchoolIcon, Mail, MessageCircle,
} from 'lucide-react';

export default function SchoolTeacherRosterView() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [school, setSchool] = useState(null);
  const [cls, setCls] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const meRes = await api.get('/auth/me');
      setMe(meRes.data);
      if (meRes.data?.school_id) {
        const [schoolRes, rosterRes] = await Promise.all([
          api.get(`/schools/${meRes.data.school_id}`),
          api.get(`/teacher/me/classes/${classId}/enrollments`),
        ]);
        setSchool(schoolRes.data);
        setCls(rosterRes.data?.school_class || null);
        setEnrollments(rosterRes.data?.enrollments || []);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Unable to load roster';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('yukti_session_token');
    const slug = school?.slug;
    navigate(slug ? `/school/${slug}/login` : '/', { replace: true });
  };

  const brandColor = school?.brand_color || '#4F46E5';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="school-teacher-roster-view"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {school?.logo_url ? (
              <img
                src={school.logo_url} alt={school.name}
                className="w-10 h-10 rounded-lg object-contain bg-white border"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: brandColor }}
              >
                <SchoolIcon size={20} />
              </div>
            )}
            <div>
              <h1 className="font-bold text-slate-900">{school?.name || 'Your School'}</h1>
              <p className="text-xs text-slate-500">{me?.email} · Teacher</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut size={16} className="mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <Button
          variant="ghost" size="sm"
          onClick={() => navigate('/school-teacher')}
          className="mb-4"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Back to dashboard
        </Button>

        {error ? (
          <div
            className="border border-red-200 bg-red-50 rounded-xl p-6 text-center"
            data-testid="roster-error"
          >
            <p className="text-red-700 font-semibold">{error}</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2
                className="text-2xl font-bold text-slate-900"
                data-testid="roster-class-name"
              >
                {cls?.name || 'Class'}
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Grade {cls?.grade}{cls?.section ? ` · Section ${cls.section}` : ''} · {cls?.academic_year}
              </p>
            </div>

            {/* Tab strip — Student List · Insights */}
            <div className="border-b mb-4 flex items-center gap-1" data-testid="roster-insights-tab-strip">
              <button
                className="px-4 py-2 text-sm font-semibold border-b-2"
                style={{ borderColor: brandColor, color: brandColor }}
                data-testid="tab-roster"
              >
                Student List
              </button>
              <button
                className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700"
                onClick={() => navigate(`/school-teacher/class/${classId}/insights`)}
                data-testid="tab-insights"
              >
                Insights
              </button>
            </div>

            {enrollments.length === 0 ? (
              <div
                className="border-2 border-dashed border-slate-200 rounded-2xl py-12 px-6 text-center bg-white/60"
                data-testid="roster-empty"
              >
                <p className="text-slate-900 font-semibold">No students enrolled yet</p>
                <p className="text-sm text-slate-600 mt-1">
                  Your school admin will enroll students soon.
                </p>
              </div>
            ) : (
              <div
                className="bg-white border rounded-xl divide-y"
                data-testid="roster-list"
              >
                {enrollments.map((e) => (
                  <div
                    key={e.enrollment_id}
                    className="px-5 py-4 flex items-center justify-between"
                    data-testid={`student-row-${e.enrollment_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold"
                        style={{ backgroundColor: brandColor }}
                      >
                        {(e.student_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{e.student_name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          <Mail size={11} /> {e.student_email}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => navigate(`/school-teacher/students/${e.enrollment_id}/chats`)}
                      data-testid={`view-student-chats-btn-${e.enrollment_id}`}
                    >
                      <MessageCircle size={14} className="mr-1.5" />
                      View Chats
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
